import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, RotateCcw, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { HUB_PROPERTIES, hubPath } from '../pages/guest-hub/hubConfig';
import { BasicsCard, RulesCard, EssentialsCard, CheckoutCard, ContactsCard, HouseMapCard, GalleryCard, CustomSectionsCard } from './guidebook/GuidebookCards';
import { SectionOrderCard } from './guidebook/SectionOrderCard';
import { GuidebookQRCard, GuidebookSeoCard } from './guidebook/GuidebookSideCards';
import { GuidebookStatsCard } from './guidebook/GuidebookStatsCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGuidebookEditor = () => {
  const { propertyId } = useParams();
  const { token } = useAuth();
  const prop = HUB_PROPERTIES.find((p) => p.id === propertyId) || { id: propertyId, name: propertyId, slug: propertyId };
  const [gb, setGb] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const headers = useCallback(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/guidebook/${propertyId}`, { headers: headers() }).then((r) => r.json()).then(setGb);
  }, [propertyId, headers]);

  const set = (patch) => {
    setDirty(true);
    setGb((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  };

  const save = async () => {
    setSaving(true);
    const { id, property_id, created_at, updated_at, ...body } = gb;
    const res = await fetch(`${API_URL}/api/admin/guidebook/${propertyId}`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) });
    setSaving(false);
    if (res.ok) {
      setGb(await res.json());
      setDirty(false);
      toast({ title: `${prop.name} guidebook saved`, description: 'Guests see the changes immediately.' });
    } else {
      toast({ title: 'Save failed', description: (await res.json().catch(() => ({}))).detail?.toString() || 'Please try again', variant: 'destructive' });
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset this guidebook to the default content? Your edits and uploads will be removed from it.')) return;
    const res = await fetch(`${API_URL}/api/admin/guidebook/${propertyId}/reset`, { method: 'POST', headers: headers() });
    if (res.ok) { setGb(await res.json()); setDirty(false); toast({ title: 'Guidebook reset to defaults' }); }
  };

  if (!gb) return <Loader2 className="h-8 w-8 animate-spin text-slate-500" />;

  const props = { gb, set, propertyId };

  return (
    <div data-testid="admin-guidebook-editor" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin/guidebooks" data-testid="gb-back-link" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4 mr-1" />All guidebooks</Link>
        <h2 className="text-xl font-semibold text-slate-900">{prop.name} Guidebook</h2>
        <div className="ml-auto flex items-center gap-2">
          <a href={hubPath(prop.slug, 'guidebook')} target="_blank" rel="noreferrer" data-testid="gb-preview-link"><Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-1" />Preview</Button></a>
          <Button variant="outline" size="sm" data-testid="gb-reset-btn" onClick={reset}><RotateCcw className="h-4 w-4 mr-1" />Reset</Button>
          <Button data-testid="gb-save-btn" size="sm" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />{dirty ? 'Save changes' : 'Saved'}</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="space-y-4">
          <BasicsCard {...props} />
          <RulesCard {...props} />
          <EssentialsCard {...props} />
          <HouseMapCard {...props} />
          <GalleryCard {...props} />
          <CheckoutCard {...props} />
          <ContactsCard {...props} />
          <CustomSectionsCard {...props} />
        </div>
        <div className="space-y-4 lg:sticky lg:top-20">
          <SectionOrderCard gb={gb} onChange={set} />
          <GuidebookStatsCard propertyId={propertyId} gb={gb} />
          <GuidebookQRCard prop={prop} />
          <GuidebookSeoCard prop={prop} />
        </div>
      </div>

      {dirty && (
        <div className="fixed bottom-4 right-4 z-40 lg:left-[17rem]">
          <Button data-testid="gb-save-floating" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900 shadow-lg">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save changes</>}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminGuidebookEditor;
